import { EstadoVazio } from '../../components/Estados'
import { IconeAlerta } from '../../components/Icones'
import { TEXTOS } from '../../lib/glossario'
import type { OcorrenciaNoRegistro, RegistroDeOcorrencias as Registro } from '../../types/relatorios'
import { competenciaPorExtenso } from '../chamados/competencia'
import { dataPorExtenso } from '../iad/pontuacao'

/**
 * O registro de ocorrências administrativas — **contínuo, e não por competência**.
 *
 * O contrato pune a **reincidência** com multa, e reincidência não se enxerga num mês: a mesma
 * infração repetida em três competências é um documento; espalhada em três telas, é três
 * observações que ninguém liga.
 *
 * **As retiradas continuam na lista.** O que foi imputado a alguém e depois retirado é justamente o
 * que o processo administrativo precisa poder ver.
 *
 * @param props o registro inteiro
 * @returns a lista contínua
 */
export function RegistroDeOcorrencias({ registro }: { registro: Registro }) {
  const t = TEXTOS.relatorios.ocorrencias

  if (registro.ocorrencias.length === 0) {
    return <EstadoVazio icone={<IconeAlerta className="h-9 w-9" />} titulo={t.vazio.titulo} ajuda={t.vazio.ajuda} />
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-700">
        {t.resumo(registro.pontosVivos, registro.ocorrencias.length)}
        {registro.revogadas > 0 && <span className="text-slate-500"> {t.retiradas(registro.revogadas)}</span>}
      </p>

      <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
        {registro.ocorrencias.map((o, posicao) => (
          <Linha key={`${o.competencia}-${o.item}-${o.ocorridoEm}-${posicao}`} ocorrencia={o} />
        ))}
      </ul>
    </div>
  )
}

/** Uma ocorrência do contrato inteiro, com a competência em que ela contou. */
function Linha({ ocorrencia }: { ocorrencia: OcorrenciaNoRegistro }) {
  const t = TEXTOS.relatorios.ocorrencias
  const viva = ocorrencia.revogadaEm == null

  return (
    <li className={viva ? 'space-y-1.5 px-3 py-3 text-sm' : 'space-y-1.5 bg-slate-50 px-3 py-3 text-sm'}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          <span className="font-medium text-slate-900">Item {ocorrencia.item}</span>{' '}
          <span className="text-slate-500">
            · {dataPorExtenso(ocorrencia.ocorridoEm)} · {t.contouEm(competenciaPorExtenso(ocorrencia.competencia))}
          </span>
          {ocorrencia.gmudCodigo && <span className="text-slate-500"> · {ocorrencia.gmudCodigo}</span>}
          <p className="mt-0.5 text-xs text-slate-600">{ocorrencia.infracao}</p>
        </div>
        <span
          className={
            viva
              ? 'whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold tabular-nums text-red-900'
              : 'whitespace-nowrap rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-600 line-through'
          }
        >
          {ocorrencia.pontos} pts
        </span>
      </div>

      <p className="text-slate-700">{ocorrencia.descricao}</p>

      {ocorrencia.contestacao && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <span className="font-semibold">{t.contestacao}</span> {ocorrencia.contestacao}
        </p>
      )}
      {!viva && (
        <p className="rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-700">
          {t.retirada} {ocorrencia.revogacaoMotivo}
        </p>
      )}
    </li>
  )
}
